/*
 * Copyright 2018 Teralytics
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { DeckGL } from '@deck.gl/react';
import FlowMapLayer, { PickingType } from '@flowmap.gl/core';
import * as React from 'react';
import { StaticMap } from 'react-map-gl';
var FLOW_MAP_LAYER_ID = 'flow-map-layer';
var ESC_KEY = 'Escape';
var FlowMap = /** @class */ (function (_super) {
    __extends(FlowMap, _super);
    function FlowMap() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            viewState: _this.props.initialViewState,
            time: 0,
        };
        _this.animationFrame = -1;
        _this.animate = function () {
            var loopLength = 1800; // unit corresponds to the timestamp in source data
            var animationSpeed = 30; // unit time per second
            var timestamp = Date.now() / 1000;
            var loopTime = loopLength / animationSpeed;
            _this.setState({
                time: ((timestamp % loopTime) / loopTime) * loopLength,
            });
            _this.animationFrame = window.requestAnimationFrame(_this.animate);
        };
        _this.handleFlowMapHover = function (info) {
            var type = info.type, object = info.object;
            switch (type) {
                case PickingType.FLOW: {
                    if (!object) {
                        _this.highlight(undefined);
                    }
                    else {
                        _this.highlight({
                            type: "flow" /* FLOW */,
                            flow: object,
                        }, info);
                    }
                    break;
                }
                case PickingType.LOCATION_AREA:
                case PickingType.LOCATION: {
                    var getLocationId = _this.props.getLocationId;
                    if (!object) {
                        _this.highlight(undefined);
                    }
                    else {
                        _this.highlight({
                            type: type === PickingType.LOCATION_AREA ? "locationArea" /* LOCATION_AREA */ : "location" /* LOCATION */,
                            locationId: (getLocationId || FlowMapLayer.defaultProps.getLocationId.value)(object),
                        }, info);
                    }
                    break;
                }
            }
        };
        _this.handleFlowMapClick = function (_a) {
            var type = _a.type, object = _a.object;
            switch (type) {
                case PickingType.LOCATION_AREA:
                case PickingType.LOCATION: {
                    if (object) {
                        var _b = _this.props, getLocationId = _b.getLocationId, multiselect = _b.multiselect;
                        var selectedLocationIds = _this.state.selectedLocationIds;
                        var locationId_1 = (getLocationId || FlowMapLayer.defaultProps.getLocationId.value)(object);
                        var isSelected = selectedLocationIds && selectedLocationIds.indexOf(locationId_1) >= 0;
                        var next = void 0;
                        if (multiselect) {
                            if (selectedLocationIds) {
                                if (isSelected) {
                                    next = selectedLocationIds.filter(function (id) { return id !== locationId_1; });
                                }
                                else {
                                    next = __spreadArrays(selectedLocationIds, [locationId_1]);
                                }
                            }
                            else {
                                next = [locationId_1];
                            }
                        }
                        else {
                            if (isSelected) {
                                next = undefined;
                            }
                            else {
                                next = [locationId_1];
                            }
                        }
                        _this.selectLocations(next);
                        _this.highlight(undefined);
                    }
                    break;
                }
            }
        };
        _this.handleViewStateChange = function (_a) {
            var viewState = _a.viewState;
            _this.setState({
                viewState: viewState,
                highlight: undefined,
            });
            var onViewStateChange = _this.props.onViewStateChange;
            if (onViewStateChange) {
                onViewStateChange(viewState);
            }
        };
        _this.handleKeyDown = function (evt) {
            if (evt instanceof KeyboardEvent && evt.key === ESC_KEY) {
                _this.setState({
                    selectedLocationIds: undefined,
                    highlight: undefined,
                });
            }
        };
        return _this;
    }
    FlowMap.getDerivedStateFromProps = function (props, state) {
        if (props.selectedLocationIds !== state.selectedLocationIds) {
            return {
                selectedLocationIds: props.selectedLocationIds,
            };
        }
        return null;
    };
    FlowMap.prototype.componentDidMount = function () {
        document.addEventListener('keydown', this.handleKeyDown);
        var animate = this.props.animate;
        if (animate) {
            this.animate();
        }
        var onViewStateChange = this.props.onViewStateChange;
        if (onViewStateChange) {
            var viewState = this.state.viewState;
            if (viewState) {
                onViewStateChange(viewState);
            }
        }
    };
    FlowMap.prototype.componentDidUpdate = function (prevProps, prevState, snapshot) {
        var animate = this.props.animate;
        if (animate !== prevProps.animate) {
            if (animate) {
                this.animate();
            }
            else {
                this.stopAnimation();
            }
        }
    };
    FlowMap.prototype.componentWillUnmount = function () {
        document.removeEventListener('keydown', this.handleKeyDown);
        this.stopAnimation();
    };
    FlowMap.prototype.render = function () {
        var _a = this.props, mapboxAccessToken = _a.mapboxAccessToken, mapStyle = _a.mapStyle, mixBlendMode = _a.mixBlendMode;
        var flowMapLayer = this.getFlowMapLayer();
        return (React.createElement(React.Fragment, null,
            React.createElement(DeckGL, { style: { mixBlendMode: mixBlendMode }, layers: [flowMapLayer], viewState: this.state.viewState, controller: true, onViewStateChange: this.handleViewStateChange },
                React.createElement(StaticMap, { width: "100%", height: "100%", mapboxApiAccessToken: mapboxAccessToken, mapStyle: mapStyle }))));
    };
    FlowMap.prototype.stopAnimation = function () {
        if (this.animationFrame) {
            window.cancelAnimationFrame(this.animationFrame);
        }
    };
    FlowMap.prototype.getFlowMapLayer = function () {
        var _a = this.props, initialViewState = _a.initialViewState, mapboxAccessToken = _a.mapboxAccessToken, mixBlendMode = _a.mixBlendMode, multiselect = _a.multiselect, onSelected = _a.onSelected, onHighlighted = _a.onHighlighted, flowMapLayerProps = __rest(_a, ["initialViewState", "mapboxAccessToken", "mixBlendMode", "multiselect", "onSelected", "onHighlighted"]);
        var _b = this.state, highlight = _b.highlight, selectedLocationIds = _b.selectedLocationIds;
        return new FlowMapLayer(__assign(__assign({ id: FLOW_MAP_LAYER_ID, animationCurrentTime: this.state.time }, flowMapLayerProps), { selectedLocationIds: selectedLocationIds, highlightedLocationId: highlight && highlight.type === "location" /* LOCATION */ ? highlight.locationId : undefined, highlightedLocationAreaId: highlight && highlight.type === "locationArea" /* LOCATION_AREA */ ? highlight.locationId : undefined, highlightedFlow: highlight && highlight.type === "flow" /* FLOW */ ? highlight.flow : undefined, onHover: this.handleFlowMapHover, onClick: this.handleFlowMapClick }));
    };
    FlowMap.prototype.highlight = function (highlight, info) {
        this.setState({ highlight: highlight });
        var onHighlighted = this.props.onHighlighted;
        if (onHighlighted) {
            onHighlighted(highlight, info);
        }
    };
    FlowMap.prototype.selectLocations = function (selectedLocationIds) {
        this.setState(function (state) { return (__assign(__assign({}, state), { selectedLocationIds: selectedLocationIds })); });
        var onSelected = this.props.onSelected;
        if (onSelected) {
            onSelected(selectedLocationIds);
        }
    };
    FlowMap.defaultProps = {
        mixBlendMode: 'multiply',
    };
    return FlowMap;
}(React.Component));
export default FlowMap;
//# sourceMappingURL=FlowMap.js.map